import type React from 'react'
import { useEffect, useState } from 'react'

import { type UseInterceptorsInterface, useInterceptors } from 'commons/customHooks'
import { authErrors, authRetCod, errorMessages } from 'commons/messages'
import { isObjectEmpty } from 'commons/utils/object'
import { Alert, Button, Input, Modal, Password } from 'components'
import { ROUTES, URL_CONST } from 'constants/urls'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import type { DataAuthRequest } from 'services/login/interfaces'
import { handleAuth, resetPasswordRequest } from 'store/AuthContext/authSlice'
import type { ErrorMessage } from 'store/AuthContext/Types/authSliceType'
import { useDispatch, useSelector } from 'store/store'

import * as S from './styles'

interface LoginFormProps {
  cd_usua: string
  ds_senh: string
}

const initialError: ErrorMessage = {
  description: '',
  title: '',
}

// TODO: Uncomment when backend is ready
// type LoginType = 'AlpargatasUser' | 'NoAlpargastaUser'

const { forgetPassword } = ROUTES

const LoginForm: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormProps>({
    mode: 'onChange',
    resolver: undefined, //isDevelopment ? undefined : yupResolver(loginFormSchema) // TODO: Uncomment when backend is ready
  })

  const [modalExpiredPassword, setModalExpiredPassword] = useState(false)
  const [expirationError, setExpirationError] = useState<ErrorMessage>(initialError)
  // const [userLoginType, setUserLoginType] = useState<LoginType | undefined>(undefined) // TODO: Uncomment when backend is ready

  // TODO: Uncomment when backend is ready
  // const handleSetLoginType = useCallback((loginType: LoginType) => {
  //   setUserLoginType(loginType)
  // }, [])

  const {
    modalTitleErrorMessage,
    modalMessage,
    setModalTitleErrorMessage,
    setModalMessage,
  }: UseInterceptorsInterface = useInterceptors()

  const { email, isLoading, loginTries, error: loginError } = useSelector((state) => state.auth)

  const hasFormError = !isObjectEmpty(errors) || watch(['cd_usua', 'ds_senh']).some((item) => !item)

  const prepareValuesToSend = (data: LoginFormProps) => {
    const { cd_usua, ds_senh } = data

    const requestData: DataAuthRequest = {
      cd_usua: cd_usua.trim(),
      ds_senh: ds_senh.trim(),
      nr_cnpj_cpf: 0,
    }
    return requestData
  }

  const successCallback = () => navigate(URL_CONST.ROOT)

  const onSubmit = handleSubmit((data) => {
    setModalMessage('...')
    setModalTitleErrorMessage('...')
    setExpirationError(initialError)

    const requestData = prepareValuesToSend(data)
    dispatch(handleAuth(requestData, successCallback))
  })

  const handlePasswordReset = () => {
    const onSend = () => navigate(forgetPassword.getLink('validateCode'))
    dispatch(resetPasswordRequest(email, onSend))
  }

  useEffect(() => {
    if (loginError.title === authErrors[authRetCod.expiredPassword].title) {
      setModalExpiredPassword(true)
    }
  }, [loginError])

  useEffect(() => {
    if (modalTitleErrorMessage === errorMessages.expiredLogin)
      setExpirationError({
        description: modalMessage || '',
        title: modalTitleErrorMessage || '',
      })
  }, [modalTitleErrorMessage, modalMessage])

  const renderErrorAction = () => {
    if (loginTries <= 0) return null
    return (
      <>
        <br />
        <br />
        {loginTries < 3 ? (
          <>
            <strong>Tentativas restantes: </strong>
            {3 - loginTries}
          </>
        ) : (
          <>
            Para desbloquear sua conta,{' '}
            <S.AlertLink onClick={handlePasswordReset}>clique aqui</S.AlertLink> para receber um
            código de redefinição de senha.
          </>
        )}
      </>
    )
  }

  return (
    <S.Container>
      {/*
      TODO: Uncomment when backend is ready
      <S.Title design="hav-h5-text">Seja bem-vindo!</S.Title>
      <S.Subtitle design="hav-default-03-text">
        Para entrar no Portal de Vendas, selecione o seu tipo de usuário:
      </S.Subtitle>

      <S.ButtonsContainer>
        <Button
          fullWidth
          content="Sou funcionário Alpargatas"
          active={userLoginType && userLoginType === 'AlpargatasUser'}
          onClick={() => handleSetLoginType('AlpargatasUser')}
          disabled={isLoading}
        />
        <Button
          fullWidth
          content="Não sou funcionário Alpargatas"
          active={userLoginType === 'NoAlpargastaUser'}
          onClick={() => handleSetLoginType('NoAlpargastaUser')}
          disabled={isLoading}
        />
      </S.ButtonsContainer> */}

      <S.Form autoComplete="on" onSubmit={onSubmit}>
        {!!loginError.title && (
          <S.ErrorWrapper>
            <Alert
              fullWidth
              kind="error"
              message={
                <span>
                  {loginError.description}
                  {renderErrorAction()}
                </span>
              }
              title={loginError.title}
            />
          </S.ErrorWrapper>
        )}
        {!!expirationError.title && (
          <S.ErrorWrapper>
            <Alert
              fullWidth
              kind="error"
              message={expirationError.description}
              title={expirationError.title}
            />
          </S.ErrorWrapper>
        )}
        <S.FieldsWrapper>
          <Controller
            control={control}
            name="cd_usua"
            render={({ field: { name, value, onChange } }) => (
              <Input
                error={errors.cd_usua?.message}
                height={40}
                helperText={errors.cd_usua?.message}
                InputProps={{
                  autoFocus: true,
                  placeholder: 'Digite seu usuário',
                }}
                label="Usuário"
                name={name}
                onChange={onChange}
                type="text"
                value={value}
              />
            )}
          />
        </S.FieldsWrapper>
        <S.FieldsWrapper>
          <Controller
            control={control}
            name="ds_senh"
            render={({ field: { name, value, onChange } }) => (
              <Password
                error={errors.ds_senh?.message}
                height={40}
                helperText={errors.ds_senh?.message}
                label="Senha"
                name={name}
                onChange={onChange}
                placeholder="********"
                value={value}
              />
            )}
          />
        </S.FieldsWrapper>
        <S.ResetPasswordContainer>
          <S.LinkToResetPassword href={forgetPassword.base}>
            Esqueceu a senha?
          </S.LinkToResetPassword>
        </S.ResetPasswordContainer>

        <Button
          content="Entrar"
          disabled={hasFormError}
          fullWidth
          loading={isLoading}
          type="submit"
        />
      </S.Form>

      <Modal
        buttonAction={() => {
          navigate(URL_CONST.EXPIREDPASSWORD)
          setModalExpiredPassword(false)
        }}
        buttonText="Entendi"
        closeAction={() => setModalExpiredPassword(false)}
        description="Sua senha está expirada, você será redirecionado para uma página onde poderá atualizá-la."
        open={modalExpiredPassword}
        setOpen={() => {
          return
        }}
        title="Crie uma nova senha"
      />
    </S.Container>
  )
}

export default LoginForm
